"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, Compass, Code as Code2, ChartBar as BarChart3, RefreshCw, ArrowRight, CircleCheck as CheckCircle2, CalendarCheck2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface StepItem {
  number: string;
  title: string;
  tagline: string;
  description: string;
  deliverables: string[];
  icon: typeof Search;
}
const steps: StepItem[] = [{
  number: "01",
  title: "I audit your performance",
  tagline: "Uncovering immediate bottlenecks and growth gaps",
  description: "At the start of every monthly cycle, I examine your Google Search Console trends, crawl health, rank movements, and competitor gains to identify exactly what is holding pages back.",
  deliverables: ["Technical crawl & indexing check", "Keyword position tracking review", "Quick-win ranking opportunities"],
  icon: Search
}, {
  number: "02",
  title: "I build the monthly strategy",
  tagline: "Prioritised execution plan tailored to current metrics",
  description: "I turn audit findings into a clear, sprint-style task backlog. You receive a transparent breakdown of exactly which pages, technical fixes, and topics I will target over the next 30 days.",
  deliverables: ["Prioritised 30-day sprint backlog", "Target keyword & topic cluster mapping", "Direct roadmap review with me"],
  icon: Compass
}, {
  number: "03",
  title: "I implement optimisations",
  tagline: "Hands-on execution directly on your website",
  description: "I deploy your retainer hours directly into high-impact execution. From publishing technical fixes and tuning internal links to schema updates and page speed optimization, every deliverable is executed personally with dedicated, hands-on expertise.",
  deliverables: ["On-page content & metadata tuning", "Direct technical & code-level fixes", "Internal linking & structure adjustments"],
  icon: Code2
}, {
  number: "04",
  title: "I report results transparently",
  tagline: "Clear metrics tied to qualified traffic and conversions",
  description: "At month-end, I prepare a no-fluff report showing ranking shifts, organic impressions, clicks, and lead actions. We review the data together so you always know your exact return on search.",
  deliverables: ["Plain-English performance summary", "Keyword movement & revenue tracking", "Recorded video or 1-to-1 walkthrough call"],
  icon: BarChart3
}, {
  number: "05",
  title: "I refine and compound gains",
  tagline: "Iterating based on search algorithm updates and user signals",
  description: "SEO is never static. I take month-end learnings, algorithmic shifts, and user interaction signals to adjust strategy for the next cycle, compounding your organic footprint every 30 days.",
  deliverables: ["Iterative strategy calibration", "Competitor reaction monitoring", "Next cycle priorities locked in"],
  icon: RefreshCw
}];
export default function MonthlyCycle() {
  const [activeStep, setActiveStep] = useState<number>(0);
  const shouldReduceMotion = useReducedMotion();
  return <section id="monthlyseocycle" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {/* Subtle decorative background glow */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-0 h-96 w-96 rounded-full bg-secondary/50 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 left-0 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide uppercase border border-border">
            Recurring 30-Day SEO Framework
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-sans tracking-tight text-foreground mb-6">
            The 30-day SEO retainer workflow built for compounding search growth
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-serif leading-relaxed">
            Organic growth is built on continuous momentum. I work in focused
            monthly sprints to identify opportunities, execute direct fixes, and
            compound your organic visibility month after month.
          </p>
        </div>

        {/* Interactive Step Switcher for Mobile & Desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
          {steps.map((step, idx) => {
          const Icon = step.icon;
          const isActive = activeStep === idx;
          return <div key={step.number} data-index={idx} className="w-full">
                <Button type="button" variant={isActive ? "default" : "outline"} onClick={() => setActiveStep(idx)} className={`w-full h-auto py-3.5 px-3.5 flex flex-col items-start justify-start text-left rounded-lg transition-[background-color,border-color,box-shadow] ${isActive ? "bg-primary text-primary-foreground border-primary shadow-md" : "bg-card text-card-foreground border-border hover:border-primary/40 hover:bg-muted"}`}>
                  <div className="flex items-center justify-between w-full mb-2">
                    <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-primary"}`}>
                      Step {step.number}
                    </span>
                    <Icon className="size-4 opacity-80" />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold font-sans line-clamp-1">
                    {step.title.replace("I ", "")}
                  </span>
                </Button>
              </div>;
        })}
        </div>

        {/* Active Step Feature Card */}
        <motion.div key={activeStep} initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: "easeOut"
      }} className="mb-16">
          <Card className="border border-border bg-card/80 backdrop-blur-xs shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-6 sm:p-8 lg:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                {/* Left Column: Details */}
                <div className="lg:col-span-7 space-y-5">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center size-10 rounded-full bg-primary text-primary-foreground font-mono font-bold text-sm">
                      {steps[activeStep].number}
                    </span>
                    <span className="text-xs sm:text-sm font-medium uppercase tracking-wider text-muted-foreground font-mono">
                      Phase {activeStep + 1} of 5
                    </span>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-bold font-sans text-foreground">
                    {steps[activeStep].title}
                  </h3>

                  <p className="text-sm sm:text-base font-medium text-primary">
                    {steps[activeStep].tagline}
                  </p>

                  <p className="text-sm sm:text-base text-muted-foreground font-serif leading-relaxed">
                    {steps[activeStep].description}
                  </p>

                  <div className="pt-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3">
                      Key Deliverables This Phase:
                    </h4>
                    <ul className="space-y-2.5">
                      {steps[activeStep].deliverables.map((item, dIdx) => <li key={dIdx} data-index={dIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>

                {/* Right Column: Visual Process Highlight */}
                <div className="lg:col-span-5 flex flex-col justify-between h-full bg-muted/50 rounded-xl p-6 border border-border">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-3 border-b border-border">
                      <span className="text-xs font-medium text-muted-foreground">
                        Sprint Cadence
                      </span>
                      <span className="text-xs font-semibold text-primary font-mono">
                        Monthly Recurring
                      </span>
                    </div>

                    <div className="p-4 rounded-lg bg-background border border-border space-y-2">
                      <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                        <CalendarCheck2 className="size-4 text-primary" />
                        <span>Direct Consultant Execution</span>
                      </div>
                      <p className="text-xs text-muted-foreground font-serif">
                        Every technical fix, keyword strategy, and review is
                        executed personally by Yassine Essaadi.
                      </p>
                    </div>

                    <div className="p-4 rounded-lg bg-background border border-border space-y-2">
                      <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                        <RefreshCw className="size-4 text-primary" />
                        <span>Continuous Compounding</span>
                      </div>
                      <p className="text-xs text-muted-foreground font-serif">
                        Once Phase 05 concludes, the learnings feed straight
                        back into Phase 01 for relentless momentum.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Ready to start cycle 01?
                    </span>
                    <Button asChild size="sm" variant="default">
                      <Link to="/ContactPage">
                        Book free consultation
                        <ArrowRight className="size-3.5 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Full 5-Step Grid Overview for Quick Skimming */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {steps.map((step, sIdx) => {
          const Icon = step.icon;
          return <div key={step.number} data-index={sIdx}>
                <div className="h-full flex flex-col justify-between p-5 rounded-xl border border-border bg-card hover:border-primary/30 transition-[border-color,box-shadow]">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-bold font-mono text-primary">
                        {step.number}
                      </span>
                      <Icon className="size-4 text-muted-foreground" />
                    </div>
                    <h4 className="text-sm font-semibold font-sans text-foreground mb-2">
                      {step.title}
                    </h4>
                    <p className="text-xs text-muted-foreground font-serif line-clamp-3">
                      {step.description}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-border/60">
                    <span className="text-[11px] font-mono text-muted-foreground">
                      Continuous loop
                    </span>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Bottom CTA Strip */}
        <div className="mt-14 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <p className="text-sm sm:text-base font-semibold text-foreground font-sans">
              Flexible monthly SEO support that can adapt as your priorities change.
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground font-serif">
              Rolling 30-day retainer cycles, clear sprint deliverables, and direct consultant execution without agency markups.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm">
              <Link to="/ServicesPage">Explore all services</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/ContactPage">
                Book a consultation
                <ArrowRight className="size-3.5 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}